<script setup lang="ts">
  import { onMounted, onUnmounted } from "vue";
  import { Spinner, useNotification } from "@ui-kit";
  import { useBarcode, usePowerRfid, useReadRfid } from "@plugins";
  import { WTFIRInputType } from "@src/api";
  import { useSpinner } from "@src/hooks";
  import { ScanScreen, ScanScreenSuccess, ScanScreenError, ScanScreenStart, Quarantine } from "@src/components";
  import { useCheckPairs } from "./useCheckPairs";
  import { default as CheckPairsSecondStep } from "./CheckPairsSecondStep.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  const notification = useNotification();
  const barcode = useBarcode();
  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const { steps, data } = useCheckPairs();
  const { visible: visibleSpinner } = useSpinner([data.isLoading]);

  const scanDataFirstStep = async (inputScanData: string, inputScanType: WTFIRInputType) => {
    if (data.isLoading.value || !inputScanData) return;

    data.set({
      isFirstScannedRfid: inputScanType === WTFIRInputType.rfid,
      [inputScanType]: { value: inputScanData },
    });

    await data.load(inputScanType);

    const isValidation = data.validation();
    const checkResult = data.check();

    if (checkResult.message) {
      notification.show({ title: checkResult.message, type: checkResult.type as "success" | "error" | "warning" });
    }

    if (isValidation && checkResult.action === "next") {
      setTimeout(() => {
        steps.set("second", "start");
        handleRemoveListener();
      }, 300);
    } else {
      steps.set("first", "not-found");
    }
  };

  const handleAddListener = async () => {
    barcode.subscribe((inputBarcodeData: string) => {
      scanDataFirstStep(inputBarcodeData, WTFIRInputType.barcode);
    });
    readRfid.subscribe((inputRfidData) => {
      scanDataFirstStep(inputRfidData.value, WTFIRInputType.rfid);
    });
  };

  const handleRemoveListener = () => {
    barcode.unsubscribe();
    readRfid.unsubscribe();
  };

  const handleAgain = () => {
    data.reset();
    steps.set("first", "start");
    handleAddListener();
  };

  onMounted(async () => {
    powerRfid.set(1);
    steps.set("first", "start");

    handleAddListener();
  });

  onUnmounted(async () => {
    handleRemoveListener();
    steps.reset();
    data.reset();

    powerRfid.set(powerRfid.prevPower.value);
  });
</script>

<template>
  <div class="widget-check-pairs" data-testid="check-pairs">
    <Quarantine
      v-if="steps.value.value.quarantine"
      @close="emit('close')"
      @click:again="
        () => {
          handleAgain();
          steps.setQuarantine(false);
        }
      "
    />
    <ScanScreen v-else-if="steps.value.value.first" :modal="{ subtitle: $t('КИЗ или EPC') }" @close="emit('close')">
      <Spinner v-if="visibleSpinner" style="width: 44px; height: 44px" />
      <ScanScreenError v-else-if="steps.value.value.first?.status === 'not-found'" />
      <ScanScreenSuccess v-else-if="steps.value.value.first?.status === 'success'" />
      <ScanScreenStart v-else />
    </ScanScreen>
    <CheckPairsSecondStep v-else-if="steps.value.value.second" @close="emit('close')" @click:back="handleAgain" />
  </div>
</template>

<style scoped>
  .widget-check-pairs {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }
</style>
